// @flow
import Loading from 'components/Loading'
import useInsuranceCompanies from 'components/ResourceFill/hooks/useInsuranceCompanies'
import useRedirects from 'components/ResourceFill/hooks/useRedirects'
import useVisited from 'components/ResourceFill/hooks/useVisited'
import { ResourceResponseContext } from 'components/ResourceFill/providers/ResourceResponseProvider'
import { ResourceSessionsContext } from 'components/ResourceFill/providers/ResourceSessionsProvider'
import { ResourceSharedContext } from 'components/ResourceFill/providers/ResourceSharedProvider'
import { ResourceSubmitContext } from 'components/ResourceFill/providers/ResourceSubmitProvider'
import ResourceFillSaveForLaterModal from 'components/ResourceFill/ResourceFillSaveForLaterModal'
import ResourceFillSaveForLaterSuccess from 'components/ResourceFill/ResourceFillSaveForLaterSuccess'
import * as React from 'react'
import { useHistory } from 'react-router-dom'
import FormScreenContainer from 'ui/form/FormScreenContainer'

import useSearchParams from '@/hooks/useSearchParams'

import { ModalAreYouStillHere } from '../Modal/ModalAreYouStillHere'
import useSentryForPatients from './hooks/useSentryForPatients'
import ResourceFillBody from './ResourceFillBody'
import ResourceFillErrorPage from './ResourceFillErrorPage'
import ResourceFillFooter from './ResourceFillFooter'
import ResourceFillHeader from './ResourceFillHeader'
import ResourceFillModalSuccess from './ResourceFillModalSuccess'
import ResourceFillModalUnavailable from './ResourceFillModalUnavailable'

function ResourceFill(): React.Node {
  const { error } = React.useContext(ResourceResponseContext) || {}
  const { submitting } = React.useContext(ResourceSubmitContext) || {}
  const { mode } = React.useContext(ResourceSharedContext) || {}
  const { sessions, sessionId } =
    React.useContext(ResourceSessionsContext) || {}
  const history = useHistory()
  const params = useSearchParams()

  const isKiosk = params.get('isKiosk') === 'true'

  const currentSession = React.useMemo(() => {
    return sessions && sessionId ? sessions[sessionId] : null
  }, [sessions, sessionId])
  const isSubmitted = React.useMemo(() => {
    return !!currentSession?.isSubmitted
  }, [currentSession])

  const [saveEditLaterModalOpen, setSaveEditLaterModalOpen] =
    React.useState(false)

  const [isSaveAndEditLaterSuccess, setIsSaveAndEditLaterSuccess] =
    React.useState(false)

  useSentryForPatients()
  useInsuranceCompanies()
  useVisited()
  useRedirects()

  React.useEffect(() => {
    const beforeUnload = (e: BeforeUnloadEvent) => {
      if (mode === 'client' && !isSubmitted) {
        e.preventDefault()
        e.returnValue = ''
        return ''
      }
      return undefined
    }

    window.addEventListener('beforeunload', beforeUnload)

    return () => {
      window.removeEventListener('beforeunload', beforeUnload)
    }
  }, [isSubmitted, mode])

  if (error) {
    return <ResourceFillErrorPage />
  }

  return (
    <>
      {submitting && (
        <Loading overlay skipLogoutMessage className="!h-screen" />
      )}
      <FormScreenContainer>
        <ResourceFillHeader />
        <ResourceFillBody />
        <ResourceFillFooter
          setSaveEditLaterModalOpen={setSaveEditLaterModalOpen}
        />
      </FormScreenContainer>
      <ResourceFillModalSuccess />
      <ResourceFillModalUnavailable />
      <ResourceFillSaveForLaterModal
        saveEditLaterModalOpen={saveEditLaterModalOpen}
        setIsSaveAndEditLaterSuccess={setIsSaveAndEditLaterSuccess}
        setSaveEditLaterModalOpen={setSaveEditLaterModalOpen}
      />
      <ResourceFillSaveForLaterSuccess
        isSaveAndEditLaterSuccess={isSaveAndEditLaterSuccess}
      />
      {isKiosk ? (
        <ModalAreYouStillHere
          inactiveSeconds={60}
          onInactivityDetected={() => history.go(-1)}
          onReturnToStart={() => history.go(-1)}
        />
      ) : null}
    </>
  )
}

export default ResourceFill
